/**
 * Car drawings — authored via the `/pack` toile process (plan
 * `thinking/plans/2026_08_31_toile_pack_wiring.md` Track B), wave `toile_drawings`. Narcis file-split
 * shape (Track A): one module per pack, `registry.ts` assembles them into `livePacks`.
 */
import {tagline, chips, type ToilePack} from './data'

export const carDrawingsPack: ToilePack = {
    slug: 'car-drawings',
    category: 'illustration',
    name: 'Car drawings',
    tagline: tagline('pieces', 'themes', 'styles'),
    chips: chips('themes', 'styles'),
    ratio: {w: 1216, h: 832},
    readings: [
        {key: 'classic', label: 'Classic convertible', subject: 'a 1950s convertible with chrome bumpers, tail fins and whitewall tyres, seen three-quarter front'},
        {key: 'sports', label: 'Sports car', subject: 'a low sports car with a long bonnet and wide rear wheel arches, seen in side profile'},
        {key: 'offroad', label: 'Off-road truck', subject: 'a mud-spattered off-road truck climbing a rocky track, its suspension flexed over a boulder'},
    ],
    styles: [
        {key: 'pencil', label: 'Pencil', style: 'graphite pencil drawing, soft layered shading from sharp 2H detail to deep 6B darks, visible paper tooth', palette: 'graphite gray only', framing: 'on plain white paper, the whole sheet used, a thin white margin around the drawing'},
        {key: 'ink', label: 'Pen and ink', style: 'pen and ink drawing, confident contour lines, fine crosshatching and stippling for tone', palette: 'black ink only', framing: 'on clean white paper, centered, a white margin all round'},
        {key: 'charcoal', label: 'Charcoal', style: 'charcoal drawing, smudged tonal masses with crisp accents and lifted highlights', palette: 'black charcoal on white', framing: 'on white drawing paper, the subject filling the sheet, a white edge left clear'},
    ],
    directions: ['a van', 'a motorbike', 'a racing car'],
    directionHint: 'try: a van',
    seo: {
        title: 'Car Drawings: Convertible, Sports Car, Off-Road Truck',
        metaDescription: 'Nine car drawings: a 1950s convertible, a low sports car in profile and a mud-spattered off-road truck, in pencil, pen and ink, or charcoal.',
        h1: 'Car drawings: a 1950s convertible, a sports car, an off-road truck',
        tagline: 'Three cars, each drawn in graphite pencil, pen and ink, and charcoal, with no badges or logos.',
        intro: 'Three cars: a 1950s convertible with chrome bumpers and tail fins, a low sports car in profile, and a mud-spattered off-road truck on a rocky track. Each is drawn in pencil, pen and ink, and charcoal, with no badges or logos. Type a different vehicle or a twist, such as a van, a motorbike or a racing car, and the same media carry it.',
        useCases: [
            'a garage or workshop wall print',
            'a sketchbook study of car lines',
            'a gift for a car enthusiast',
            'a reference for drawing a vehicle in perspective',
        ],
        faq: [
            {q: 'What is in this pack?', a: 'Nine drawings of three vehicles: a 1950s convertible, a low sports car in profile, and an off-road truck. Each appears in three media.'},
            {q: 'Do the drawings show badges or logos?', a: 'No. The cars are drawn with no badges or logos.'},
            {q: 'Can I ask for a different vehicle?', a: 'Yes. Type a vehicle or twist, such as a van, a motorbike or a racing car. A typed direction is a request, not a promised result.'},
            {q: 'How do the three styles differ?', a: 'Pencil shades with soft layers on plain white paper. Pen and ink uses contour lines and crosshatching. Charcoal uses smudged tone with crisp accents and lifted highlights.'},
            {q: 'Is each drawing wide or tall?', a: 'Wide. The car drawings are wider than they are tall.'},
        ],
    },
}
